import React, { useEffect, useState, useCallback, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useIntl, FormattedDate } from "react-intl";
import { FileText, CheckSquare, Square, User, Map, Trash , AlertTriangle} from "react-feather";
import {
  getOperation,
  getOperationCars,
  exportOperationKml,
  exportOperationValidation,
  downloadCarAndAssignToOperation,
  getOperationFeatureCollection,
  disableOperationReport
} from "../../api";
import Skeleton from "../../components/Skeleton";
import UserContext from "../../context/UserContext";
import "./styles.scss";
import { formatBalance } from "../../utils/format";
import CaptchaModal from "./components/CaptchaModal";
import OperationMap from "./components/OperationMap";
import ExportButton from "./components/ExportButton";
import DeletionConfirmationModal from "./components/DeletionConfirmationModal";
import Tooltip from 'rc-tooltip';

const CAPTCHA_MODAL = "CAPTCHA_MODAL";
const DELETION_CONFIRMATION_MODAL = "DELETION_CONFIRMATION_MODAL";

const OperationResults = ({ setError }) => {
  const { id } = useParams();
  const history = useHistory();
  const { formatMessage , formatNumber} = useIntl();
  const [operationResults, setOperationResults] = useState(null);
  const [detectedCars, setDetectedCars] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingKmlDownload, setLoadingKmlDownload] = useState(false);
  const [loadingJsonDownload, setLoadingJsonDownload] = useState(false);
  const [openModal, setOpenModal] = useState(null);
  const [downloadCarNumber, setDownloadCarNumber] = useState(null);
  const [reportNumberToDelete, setReportNumberToDelete] = useState(null);
  const [areaInfo, setAreaInfo] = useState(null);
  const userData = useContext(UserContext);

  const fetchResults = useCallback(() => {
    setLoading(true);
    
    getOperation(id)
      .then(({ data }) => {
        const results = { ...data.result };

        setOperationResults(results);

        getOperationCars(id)
          .then(({ data: cars }) => {
            setDetectedCars(cars.result.detalhes);
          }) 
          .finally(() => setLoading(false));
      })
      .catch(err => {
        setError(
          err?.response?.data?.message || formatMessage({ id: "erro-api" })
        );
      });
    
    getOperationFeatureCollection(id)
      .then(({ data }) => {
        setAreaInfo(data.result);
      })
  }, [id, formatMessage, setError]);

  useEffect(() => {
    userData && userData.updateBalance();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const disableReport = (reportId, operationId) => {
    disableOperationReport(reportId, operationId)
      .then(() => {
        setReportNumberToDelete(null);
        fetchResults();
      })
      .catch((err) => {
        setError(
          err?.response?.data?.message || formatMessage({ id: "erro-api" })
        );
      });
  }
  
  const downloadFile = (filename, data) => {
      let link = document.createElement("a");

      const blob = new Blob([data]);
      const fileDownloadUrl = URL.createObjectURL(blob);

      link.setAttribute('href', fileDownloadUrl);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  }

  const handleExportJsonClick = () => {
    setLoadingJsonDownload(true);
    exportOperationValidation(operationResults.B3Lote.id).then(res => {
      setLoadingJsonDownload(false);
      const data = res.data.result;
      downloadFile(
        `${operationResults.B3Lote.nome}.json`,
        JSON.stringify(data, null, 2)
      );
    })     
  }

  const handleExportKmlClick = () => {
    setLoadingKmlDownload(true);
    exportOperationKml(operationResults.B3Lote.id).then(res => {
      setLoadingKmlDownload(false);
      downloadFile(`${operationResults.B3Lote.nome}.kml`, res.data);
    });
  }

  const handleRowClick = (report) => {
    history.push(`/results/${report.id}`)
  }

  const handleSendCaptcha = (data) => {
    const body = {
      ...data,
      b3_lote_id: operationResults.B3Lote.id,
      numero_car: downloadCarNumber
    }

    downloadCarAndAssignToOperation(body)
      .then(res => {
        setOpenModal(null);
        setLoading(true);

        // getOperationCars(id)
        //   .then(({ data: cars }) => {
        //     setDetectedCars(cars.result.detalhes);
        //     setLoading(false);
        //   }) 

        getOperation(id)
          .then(({ data }) => {
            const results = { ...data.result };

            setOperationResults(results);

            getOperationCars(id)
              .then(({ data: cars }) => {
                setDetectedCars(cars.result.detalhes);
              }) 
              .finally(() => setLoading(false));
          })
          .catch(err => {
            setError(
              err?.response?.data?.message || formatMessage({ id: "erro-api" })
            );
          });

      })
      .catch(res => {
        setError('Ocorreu algum erro. Tente novamente');
        setLoading(false);
        setOpenModal(null);
      })
  };

  return (
    <>
      <h1>{formatMessage({ id: "resultado-operacao" })}</h1>

      <div className="content-box results">
        <div className="tile is-ancestor">
          <div className="tile is-ancestor is-vertical is-4">
            <p>
              <span className="bold">
                {formatMessage({ id: "nome-operacao" })}:
              </span>{" "}
              {operationResults ? (
                operationResults.B3Lote.nome || (
                  <span className="untitled">
                    {formatMessage({ id: "sem-titulo" })}
                  </span>
                )
              ) : (
                <Skeleton width="200" />
              )}
            </p>
            <p>
              <span className="bold">
                {formatMessage({ id: "contrato" })}:
              </span>{" "}
              {operationResults ? (
                operationResults.B3Lote.detalhes_json && operationResults.B3Lote.detalhes_json.contrato ? (
                  <span>
                    {operationResults.B3Lote.detalhes_json.contrato}
                  </span>
                ) : (
                  <span className="untitled">
                    {formatMessage({ id: "sem-contrato" })}
                  </span>
                )
              ) : (
                <Skeleton width="100" />
              )}
            </p>
            <p>
              <span className="bold">
                {formatMessage({ id: "referencia-bacen" })}:
              </span>{" "}
              {operationResults ? (
                operationResults.B3Lote.detalhes_json && operationResults.B3Lote.detalhes_json.referencia_bacen ? (
                  <span>
                    {operationResults.B3Lote.detalhes_json.referencia_bacen}
                  </span>
                ) : (
                  <span className="untitled">
                    {formatMessage({ id: "sem-referencia-bacen" })}
                  </span>
                )
              ) : (
                <Skeleton width="100" />
              )}
            </p>
            <p>
              <span className="bold">
                {formatMessage(
                  { id: "Documento" },
                  {
                    num: operationResults && operationResults.B3Lote.detalhes_json 
                      ? operationResults.B3Lote.detalhes_json.cpf_cnpj.length 
                      : 0
                  }
                )}
                :
              </span>{" "}
              {operationResults ? (
                operationResults.B3Lote.detalhes_json && operationResults.B3Lote.detalhes_json.cpf_cnpj ? (
                  <span>
                    {operationResults.B3Lote.detalhes_json.cpf_cnpj.map(document => (
                      <li key={document}>{document}</li>
                    ))}
                  </span>
                ) : (
                  <span className="untitled">
                    {formatMessage({ id: "sem-documento" })}
                  </span>
                )
              ) : (
                <Skeleton width="100" />
              )}
            </p>
            <p>
              <span className="bold">
                {formatMessage({ id: "data-operacao" })}:
              </span>{" "}
              {operationResults ? (
                <FormattedDate
                  value={new Date(operationResults.B3Lote.created.split())}
                />
              ) : (
                <Skeleton width="100" />
              )}
            </p>
            <p>
              <span className="bold">
                {formatMessage({ id: "area-nao-cultivada" })} (ha):
              </span>{" "}
              {operationResults ? (
                operationResults.B3Lote.detalhes_json && operationResults.B3Lote.detalhes_json.area_nao_cultivada ? (
                  <span>
                    {operationResults.B3Lote.detalhes_json.area_nao_cultivada}
                  </span>
                ) : (
                  <span className="untitled">
                    0
                  </span>
                )
              ) : (
                <Skeleton width="100" />
              )}
            </p>

            <p>
              <span className="bold">
                {formatMessage({ id: "pronaf" })}:
              </span>{" "}
              {operationResults ? (
                operationResults.B3Lote.detalhes_json && operationResults.B3Lote.detalhes_json.is_pronaf ? (
                  <span>
                    Sim
                  </span>
                ) : (
                  <span className="untitled">
                    Não
                  </span>
                )
              ) : (
                <Skeleton width="100" />
              )}
            </p>

            <p>
              <span className="bold">
                {formatMessage({ id: "modulos-fiscais" })}:
              </span>{" "}
              {operationResults ? (
                operationResults.B3Lote.soma_modulo_fiscal_car ? (
                  <span>
                    {` ${formatNumber(operationResults.B3Lote.soma_modulo_fiscal_car)} `}

                    {
                      (operationResults.B3Lote.detalhes_json && operationResults.B3Lote.detalhes_json.is_pronaf === true && operationResults.B3Lote.soma_modulo_fiscal_car > 4 ) ? (
                        <span>
                          <AlertTriangle color="red" size={18} />
                        </span>
                      ) : (
                        <span className="untitled">
                        </span>
                      )
                    }

                  </span>
                ) : (
                  <span className="untitled">
                    0
                  </span>
                )
              ) : (
                <Skeleton width="100" />
              )}
            </p>

          </div>
          <div className="area-info tile is-vertical is-8">
            {areaInfo && (
              <OperationMap
                featureCollection={areaInfo}
                className="area-map tile is-vertical"
              />
            )}
          </div>
        </div>

        <div className="tile is-ancestor is-horizontal">
          <p>
            <ExportButton
              textId="exportar-kml"
              onClick={handleExportKmlClick}
              isLoading={loadingKmlDownload}
            />
          </p>

          <p>
            <ExportButton
              textId="validacao-operacao"
              onClick={handleExportJsonClick}
              isLoading={loadingJsonDownload}
            />
          </p>
        </div>
            
        <div className="tile is-ancestor is-vertical">
          <h4>{formatMessage({ id: "validacao-compliance" })}</h4>
          {!loading && operationResults.B3ConsultasB3Lote.length === 0 ? (
            <div className="no-results">
              <FileText size="48px" />
              <p>{formatMessage({ id: "nenhuma-operacao-encontrada" })}</p>
            </div>
          ) : (
            <>
              <table className="table is-striped is-hoverable">
                <thead>
                  <tr>
                    <th>{formatMessage({ id: "data-consulta" })}</th>
                    <th>{formatMessage({ id: "nome-consulta" })}</th>
                    <th>{formatMessage({ id: "processada" })}</th>
                    <th>{formatMessage({ id: "tipos-criterio" })}</th>
                    <th>{formatMessage({ id: "alertas" })}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loading &&
                    [...Array(5)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(5)].map((_, j) => (
                          <td key={j}>
                            <Skeleton />
                          </td>
                        ))}
                      </tr>
                    ))}
                  {!loading && operationResults.B3ConsultasB3Lote.length > 0 &&
                    operationResults.B3ConsultasB3Lote.map(report => (
                      <tr
                        key={report.B3Consulta.id}
                        data-testid={`result-${report.id}`}
                        onClick={() => handleRowClick(report.B3Consulta)}
                      >
                        <td>
                          <FormattedDate
                            value={new Date(report.B3Consulta.data_consulta)}
                            year="numeric"
                            month="numeric"
                            day="numeric"
                            hour="numeric"
                            minute="numeric"
                          />
                        </td>
                        <td>
                          {report.B3Consulta.requisicao_json.nome || (
                            <span className="untitled">
                              {formatMessage({ id: "sem-titulo" })}
                            </span>
                          )}
                        </td>
                        <td>
                          {report.B3Consulta.processado ? (
                            <CheckSquare
                              title={formatMessage({
                                id: "processada"
                              })}
                            />
                          ) : (
                            <Square
                              title={formatMessage({
                                id: "nao-processada"
                              })}
                            />
                          )}
                        </td>
                        <td>
                          {report.B3Consulta.requisicao_json.cpf_cnpj &&
                            report.B3Consulta.requisicao_json.cpf_cnpj.length >
                              0 && (
                              <User
                                size="20px"
                                title={formatMessage({
                                  id: "possui-criterio-documento"
                                })}
                              />
                            )}{" "}
                          {report.B3Consulta.requisicao_json.area_id &&
                            report.B3Consulta.requisicao_json.area_id.length > 0 && (
                              <Map
                                size="20px"
                                title={formatMessage({
                                  id: "possui-criterio-area"
                                })}
                              />
                            )}
                        </td>
                        <td>
                          {report.alertas &&
                          report.alertas.length > 0 && (
                            <Tooltip
                              placement="right"
                              trigger={['hover']}
                              overlay={
                                <>
                                  {report.alertas.map(alerta => (
                                    <p key={alerta.id}>{alerta.nome}</p>
                                  ))}
                                </>
                              }
                            >
                              <AlertTriangle color="red" />
                            </Tooltip>
                          )}
                        </td>
                        <td>
                          <button 
                            className="disable-report" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setReportNumberToDelete(report.B3Consulta.id)
                              setOpenModal(DELETION_CONFIRMATION_MODAL);
                            }}
                          >
                            <Trash size={15} /> 
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        <br />

        <div className="tile is-ancestor is-vertical">
          <h4>{formatMessage({ id: "cars-detectados" })} {!loading && detectedCars ? <small>({detectedCars.length})</small> : null}</h4>
          {!loading && operationResults.B3ConsultasB3Lote.length === 0 ? (
            <div className="no-results">
              <FileText size="48px" />
              <p>{formatMessage({ id: "nenhum-car-encontrado" })}</p>
            </div>
          ) : (
            <div className="detected-cars scrollable">
              {!loading && detectedCars && detectedCars.length > 0 ? 
                detectedCars.map(car => (
                  <div key={car.gid} className="detected-cars-wrapper tile is-ancestor">
                    <div className="tile is-vertical is-10 detected-car">
                      <p>
                        <span className="bold">
                          {formatMessage({ id: "numero-car" })}:
                        </span>{" "}

                        <span>{car.cod_imovel}</span>
                      </p>
                      <p>
                        <span className="bold">
                          {formatMessage({ id: "status" })}:
                        </span>{" "}

                        <span>{car.status_imo}</span>
                      </p>
                      <p>
                        <span className="bold">
                          {formatMessage({ id: "area-car" })}:
                        </span>{" "}

                        <span>{formatBalance(car.area * 100)} ha</span>
                      </p>
                      <p>
                        <span className="bold">
                          {formatMessage({ id: "tipo-imovel" })}:
                        </span>{" "}

                        <span>{car.tipo_imove}</span>
                      </p>
                      <p>
                        <span className="bold">
                          {formatMessage({ id: "modulos-fiscais" })}:
                        </span>{" "}

                        <span>{formatBalance(car.m_fiscal *100)}</span>
                      </p>
                      <p>
                        <span className="bold">
                          {formatMessage({ id: "area-intersecao" })}:
                        </span>{" "}

                        <span>{formatBalance(car.area_ha_intersecao * 100)} ha</span>
                      </p>
                    </div>
                    <div className="tile is-vertical is-2 center">
                      {car.is_vinculado ? (
                        <span className="untitled">{formatMessage({ id: "vinculado" })}</span>
                      ) : (
                        <ExportButton
                          textId="vincular"
                          onClick={() => {
                            setOpenModal(CAPTCHA_MODAL)
                            setDownloadCarNumber(car.cod_imovel)
                          }
                        } 
                        />
                      )}
                    </div>
                  </div>
                ))
               : (
                <div className="no-results">
                  <FileText size="48px" />
                  <p>{formatMessage({ id: "nenhum-car-encontrado" })}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {openModal === DELETION_CONFIRMATION_MODAL && (
        <DeletionConfirmationModal
          onClose={() => setOpenModal(null)}
          onConfirmation={() => {
            setOpenModal(null);
            disableReport(reportNumberToDelete, operationResults.B3Lote.id);
          }}
        />
      )}

      {openModal === CAPTCHA_MODAL && (
        <CaptchaModal
          onClose={() => {
            setOpenModal(null);
            setDownloadCarNumber(null);
          }}
          onSend={handleSendCaptcha}
        />
      )}
    </>
  );
};

export default OperationResults;
